import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck, CheckCircle2, MapPin, User2, CalendarDays, Plus, Printer, Map } from "lucide-react";
import api from "@/lib/api";
import { formatJMD, STATUS_META, fmtDate, todayStr } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export const LogisticsTab = ({ orders, deliveries, reload }) => {
  const [runDate, setRunDate] = useState(todayStr());
  const [driver, setDriver] = useState("");
  const [picked, setPicked] = useState([]);
  const navigate = useNavigate();

  // Orders ready to be assigned (not yet on a run, not delivered/cancelled)
  const assignable = orders.filter(
    (o) => !o.delivery_run_id && ["pending", "confirmed"].includes(o.status)
  );

  const toggle = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const assign = async () => {
    if (picked.length === 0) {
      toast.error("Select at least one order");
      return;
    }
    try {
      await api.post("/deliveries/assign", { order_ids: picked, run_date: runDate, driver });
      toast.success("Delivery run created");
      setPicked([]);
      setDriver("");
      reload();
    } catch {
      toast.error("Could not create run");
    }
  };

  const complete = async (id) => {
    try {
      await api.patch(`/deliveries/${id}/complete`);
      toast.success("Run completed · orders delivered");
      reload();
    } catch {
      toast.error("Could not complete run");
    }
  };

  const markDelivered = async (id) => {
    try {
      await api.patch(`/orders/${id}/delivered`);
      toast.success("Marked delivered");
      reload();
    } catch {
      toast.error("Failed");
    }
  };

  const dayRuns = deliveries.filter((d) => d.run_date === runDate);

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Assign panel */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-display font-bold text-slate-900 flex items-center gap-2 mb-4">
          <Truck className="w-5 h-5 text-sky-600" /> Build a Delivery Run
        </h3>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <Label>Run date</Label>
            <Input type="date" data-testid="logistics-run-date" value={runDate} onChange={(e) => setRunDate(e.target.value)} className="h-10 rounded-xl" />
          </div>
          <div>
            <Label>Driver</Label>
            <Input placeholder="Driver name" data-testid="logistics-driver" value={driver} onChange={(e) => setDriver(e.target.value)} className="h-10 rounded-xl" />
          </div>
        </div>

        <div className="space-y-2 max-h-80 overflow-auto pr-1">
          {assignable.map((o) => (
            <label
              key={o.id}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                picked.includes(o.id) ? "border-sky-400 bg-sky-50" : "border-slate-200 hover:bg-slate-50"
              }`}
            >
              <Checkbox checked={picked.includes(o.id)} onCheckedChange={() => toggle(o.id)} data-testid={`logistics-pick-${o.id}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono-cw text-sm font-bold text-sky-700">{o.order_code}</span>
                  <Badge className={`border text-[10px] ${STATUS_META[o.status]?.cls}`}>{STATUS_META[o.status]?.label}</Badge>
                </div>
                <div className="text-sm font-semibold text-slate-800">{o.customer_name}</div>
                <div className="text-xs text-slate-400 truncate flex items-center gap-1"><MapPin className="w-3 h-3" />{o.address}</div>
              </div>
              <div className="font-mono-cw text-sm font-semibold text-slate-600">{formatJMD(o.total_amount)}</div>
            </label>
          ))}
          {assignable.length === 0 && (
            <div className="text-sm text-slate-400 text-center py-8">No unassigned orders. All caught up! 🎉</div>
          )}
        </div>

        <Button
          data-testid="admin-btn-assign-delivery"
          onClick={assign}
          disabled={picked.length === 0}
          className="w-full mt-4 h-11 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-semibold"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Assign {picked.length > 0 ? `${picked.length} order(s)` : "to run"}
        </Button>
      </div>

      {/* Daily manifest */}
      <div>
        <h3 className="font-display font-bold text-slate-900 flex items-center gap-2 mb-4">
          <CalendarDays className="w-5 h-5 text-sky-600" /> Daily Delivery List · {fmtDate(runDate)}
        </h3>
        <div className="space-y-4" data-testid="logistics-daily-list">
          {dayRuns.map((run) => (
            <div key={run.id} className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <User2 className="w-4 h-4 text-slate-400" />
                  <span className="font-semibold text-slate-800">{run.driver}</span>
                  <Badge className={run.status === "completed" ? "bg-emerald-100 text-emerald-700 border border-emerald-200" : "bg-indigo-100 text-indigo-700 border border-indigo-200"}>
                    {run.status === "completed" ? "Completed" : "Scheduled"}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" data-testid={`route-run-${run.id}`} onClick={() => navigate(`/admin/route/${run.id}`)} className="h-8 rounded-lg text-xs">
                    <Map className="w-3.5 h-3.5 mr-1" /> Route map
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`manifest-run-${run.id}`} onClick={() => navigate(`/admin/manifest/${run.id}`)} className="h-8 rounded-lg text-xs">
                    <Printer className="w-3.5 h-3.5 mr-1" /> Run sheet
                  </Button>
                  {run.status !== "completed" && (
                    <Button size="sm" data-testid={`complete-run-${run.id}`} onClick={() => complete(run.id)} className="h-8 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Complete run
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                {run.orders.map((o) => (
                  <div key={o.id} className="flex items-center justify-between text-sm p-2 rounded-lg bg-slate-50">
                    <div className="min-w-0">
                      <span className="font-mono-cw text-sky-700 font-bold mr-2">{o.order_code}</span>
                      <span className="font-semibold text-slate-800">{o.customer_name}</span>
                      <div className="text-xs text-slate-400 truncate">{o.address}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={`border text-[10px] ${STATUS_META[o.status]?.cls}`}>{STATUS_META[o.status]?.label}</Badge>
                      {o.status !== "delivered" && (
                        <Button size="sm" variant="ghost" data-testid={`admin-btn-mark-delivered-${o.id}`} onClick={() => markDelivered(o.id)} className="h-7 text-xs text-emerald-600">
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Delivered
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {dayRuns.length === 0 && (
            <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-400">
              <Truck className="w-10 h-10 mx-auto mb-3 opacity-40" /> No runs scheduled for this date.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
